<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { formatDurationMinutes } from 'dashboard/components-next/AssignmentPolicy/helpers';

const { t } = useI18n();

const fairDistributionLimit = defineModel('fairDistributionLimit', {
  type: Number,
  default: 100,
  set(value) {
    return Number(value) || 0;
  },
});

// The model value is in seconds (for the backend/DB)
// DurationInput works in minutes internally
// We need to convert between seconds and minutes
const fairDistributionWindow = defineModel('fairDistributionWindow', {
  type: Number,
  default: 3600,
  set(value) {
    return Number(value) || 0;
  },
});

const windowUnit = ref(DURATION_UNITS.MINUTES);

// Convert seconds to minutes for DurationInput
const windowInMinutes = computed({
  get() {
    return Math.floor((fairDistributionWindow.value || 0) / 60);
  },
  set(minutes) {
    fairDistributionWindow.value = minutes * 60;
  },
});

// Detect unit based on minutes (converted from seconds)
const detectUnit = minutes => {
  const m = Number(minutes) || 0;
  if (m === 0) return DURATION_UNITS.MINUTES;
  if (m % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (m % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

const summary = computed(() => {
  const window = formatDurationMinutes(windowInMinutes.value, t);
  if (!fairDistributionLimit.value || !window) return '';
  return t('ASSIGNMENT_POLICY.REDESIGN.ASSIGNMENT.FAIR', {
    limit: fairDistributionLimit.value,
    window,
  });
});

onMounted(() => {
  windowUnit.value = detectUnit(windowInMinutes.value);
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Input
        v-model="fairDistributionLimit"
        type="number"
        :label="
          t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.FAIR_DISTRIBUTION.INPUT_MAX'
          )
        "
        placeholder="100"
        min="1"
        max="100000"
        class="w-full"
        data-test-id="fair-distribution-limit"
      />

      <div class="flex min-w-0 flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">
          {{
            t(
              'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.FAIR_DISTRIBUTION.DURATION'
            )
          }}
        </span>
        <div
          class="flex items-center gap-2 [&>select]:!bg-n-alpha-2 [&>select]:!outline-none [&>select]:hover:brightness-110"
        >
          <!-- allow 10 mins to 999 days (in minutes) -->
          <DurationInput
            v-model:model-value="windowInMinutes"
            v-model:unit="windowUnit"
            :min="10"
            :max="1438560"
          />
        </div>
      </div>
    </div>
    <p
      v-if="summary"
      class="mb-0 flex items-center gap-2 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-11"
      data-test-id="fair-distribution-summary"
    >
      <Icon
        icon="i-hugeicons-balance-scale"
        class="size-4 shrink-0 text-n-slate-11"
      />
      {{ summary }}
    </p>
  </div>
</template>
